import React from 'react';
import {
    Container,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Box,
    Chip
} from '@mui/material';
import {
    Assessment as ReportIcon,
    Receipt as ReceiptIcon,
    TrendingUp as TrendingIcon,
    PieChart as PieChartIcon,
    BarChart as BarChartIcon,
    Timeline as TimelineIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const ReportsDashboard = () => {
    const reports = [
        {
            id: 1,
            title: 'Gas Transaction Report',
            description: 'Comprehensive analysis of gas transactions with filtering and summary statistics',
            icon: <ReceiptIcon sx={{ fontSize: '2rem' }} />,
            path: '/reports/gas-transactions',
            color: 'primary',
            features: ['Filtering', 'Sorting', 'Pagination', 'Summary Stats', 'Export Ready']
        },
        {
            id: 2,
            title: 'Customer Report',
            description: 'Comprehensive customer analysis with distributor statistics and filtering options',
            icon: <TrendingIcon sx={{ fontSize: '2rem' }} />,
            path: '/reports/customers',
            color: 'success',
            features: ['Filtering', 'Sorting', 'Distributor Analysis', 'Deposit Tracking', 'Export Ready']
        },
        {
            id: 3,
            title: 'Monthly Summary',
            description: 'Month-wise breakdown of transactions and business metrics',
            icon: <PieChartIcon sx={{ fontSize: '2rem' }} />,
            path: '/reports/monthly-summary',
            color: 'info',
            features: ['Coming Soon'],
            disabled: true
        },
        {
            id: 4,
            title: 'Performance Dashboard',
            description: 'Real-time performance metrics and KPI tracking',
            icon: <BarChartIcon sx={{ fontSize: '2rem' }} />,
            path: '/reports/performance',
            color: 'warning',
            features: ['Coming Soon'],
            disabled: true
        }
    ];

    return (
        <Container maxWidth="xl">
            <Box sx={{ mt: 4, mb: 4 }}>
                {/* Header */}
                <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ReportIcon sx={{ mr: 2, fontSize: '3rem', color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h3" gutterBottom>
                                Reports Dashboard
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                                Comprehensive business intelligence and analytics
                            </Typography>
                        </Box>
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800 }}>
                        Access detailed reports and analytics to gain insights into your gas distribution business. 
                        Monitor transactions, analyze customer behavior, and track performance metrics.
                    </Typography>
                </Paper>

                {/* Reports Grid */}
                <Grid container spacing={3}>
                    {reports.map((report) => (
                        <Grid item xs={12} sm={6} md={6} lg={4} key={report.id}>
                            <Card 
                                sx={{ 
                                    height: '100%', 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    opacity: report.disabled ? 0.6 : 1,
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: report.disabled ? 'none' : 'translateY(-4px)',
                                        boxShadow: report.disabled ? 1 : 4
                                    }
                                }}
                                elevation={report.disabled ? 1 : 3}
                            >
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{ 
                                            p: 1, 
                                            borderRadius: 2, 
                                            bgcolor: `${report.color}.50`,
                                            color: `${report.color}.main`,
                                            mr: 2 
                                        }}>
                                            {report.icon}
                                        </Box>
                                        <Typography variant="h6" component="h2" gutterBottom>
                                            {report.title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 48 }}>
                                        {report.description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {report.features.map((feature, index) => (
                                            <Chip
                                                key={index}
                                                label={feature}
                                                size="small"
                                                variant="outlined"
                                                color={feature === 'Coming Soon' ? 'default' : report.color}
                                            />
                                        ))}
                                    </Box>
                                </CardContent>
                                <CardActions sx={{ p: 2, pt: 0 }}>
                                    <Button
                                        component={Link}
                                        to={report.path}
                                        variant={report.disabled ? 'outlined' : 'contained'}
                                        color={report.color}
                                        fullWidth
                                        disabled={report.disabled}
                                        startIcon={<TimelineIcon />}
                                    >
                                        {report.disabled ? 'Coming Soon' : 'View Report'}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Quick Stats */}
                <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Available Reports Overview
                    </Typography>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="primary.main">
                                    2
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Active Reports
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="success.main">
                                    2
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Coming Soon
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="info.main">
                                    ∞
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Data Points
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="warning.main">
                                    24/7
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Availability
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        </Container>
    );
};

export default ReportsDashboard;